import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../../interceptors";
import { toast } from "../../../components/shared/toast";
import LoadingState from "../../../components/shared/LoadingState";
import ErrorState from "../../../components/shared/ErrorState";

// ── /admin/settings/candidate-experience ─────────────────────────────────
// Two workspace-level toggles backed by amplehire_organization_t (V020):
//   - candidateFeedbackEnabled
//   - candidateFeedbackShowAfterDecision
//
// Each toggle PATCH-saves on change; the page renders a "Saved" toast on
// success and rolls the toggle back on failure.

export default function CandidateExperienceSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    document.title = "Candidate experience - Ample Hire";
  }, []);

  const load = () => {
    setLoading(true);
    setErrorStatus(null);
    setErrorMsg(null);
    axiosInstance
      .get("/job-posting/workspace-settings/candidate-feedback")
      .then((r) => {
        if (r?.data?.success) setSettings(r.data.settings || {});
        else {
          setErrorStatus(500);
          setErrorMsg(r?.data?.message || "Couldn't load settings");
        }
      })
      .catch((e) => {
        // Interceptor normalizes to flat {status, message, data}; fall back
        // to the raw axios shape for safety.
        setErrorStatus(e?.status || e?.response?.status || 500);
        setErrorMsg(e?.data?.message || e?.response?.data?.message || e?.message || "Couldn't load settings");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const save = async (key, value) => {
    // Optimistic update — flip first, roll back on failure.
    const previous = settings?.[key];
    setSettings({ ...settings, [key]: value });
    setSavingKey(key);
    try {
      const r = await axiosInstance.put(
        "/job-posting/workspace-settings/candidate-feedback",
        { [key]: value },
      );
      if (r?.data?.success) {
        setSettings(r.data.settings || { ...settings, [key]: value });
        toast.success("Setting saved");
      } else {
        setSettings({ ...settings, [key]: previous });
        const code = r?.data?.code;
        const description = code === "no_org_row"
          ? "This workspace has no organization profile yet."
          : r?.data?.message || "Unknown error.";
        toast.error("Couldn't save", { description });
      }
    } catch (e) {
      setSettings({ ...settings, [key]: previous });
      const code = e?.data?.code || e?.response?.data?.code;
      const description = code === "no_org_row"
        ? "This workspace has no organization profile yet."
        : e?.data?.message || e?.response?.data?.message || e?.message || "Try again in a moment.";
      toast.error("Couldn't save", { description });
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="page-enter max-w-3xl space-y-4">
      <header>
        <Link
          to="/admin/settings"
          className="text-xs text-vf-primary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
        >
          ← All settings
        </Link>
        <h1 className="text-2xl font-semibold text-vf-base mt-2 mb-1">Candidate experience</h1>
        <p className="text-sm text-vf-muted m-0">
          Control what feedback candidates see after their interview is scored.
        </p>
      </header>

      {loading ? (
        <div className="bg-white border border-subtle rounded-card p-4">
          <LoadingState variant="skeleton" shape="row" count={2} />
        </div>
      ) : errorStatus ? (
        <ErrorState status={errorStatus} description={errorMsg} retry={load} />
      ) : (
        <section className="bg-white border border-subtle rounded-card divide-y divide-subtle">
          <ToggleRow
            label="Show candidates their AI feedback report after decision"
            description="When ON, candidates see a qualitative score band plus 3 strengths and 2 growth areas with coaching tips. Recruiter scorecards remain private either way."
            value={settings?.candidateFeedbackEnabled ?? true}
            saving={savingKey === "candidateFeedbackEnabled"}
            onChange={(v) => save("candidateFeedbackEnabled", v)}
          />
          <ToggleRow
            label="Show feedback only after hiring decision is made"
            description="When ON, the feedback report appears only after the candidate is shortlisted or rejected — not immediately after scoring."
            value={settings?.candidateFeedbackShowAfterDecision ?? true}
            saving={savingKey === "candidateFeedbackShowAfterDecision"}
            onChange={(v) => save("candidateFeedbackShowAfterDecision", v)}
          />
        </section>
      )}
    </div>
  );
}

function ToggleRow({ label, description, value, saving, onChange }) {
  return (
    <div className="flex items-start gap-4 p-5">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-vf-base">{label}</div>
        <p className="text-xs text-vf-muted mt-1 m-0">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={label}
        disabled={saving}
        onClick={() => onChange(!value)}
        className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary ${
          value ? "bg-vf-primary" : "bg-slate-300"
        } ${saving ? "opacity-60 cursor-wait" : "cursor-pointer"}`}
      >
        <span
          aria-hidden="true"
          className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
            value ? "translate-x-[22px]" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}
